// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The sections a route draws under its own content (S3-07).
//
// WHY THIS COMPONENT EXISTS RATHER THAN THREE COPIES OF THE SAME JSX
// ------------------------------------------------------------------
// The Search Console read of 2026-09-06 found the arcade, pricing and the Builder carrying 21, 25
// and 26 words of crawlable text between them — the three pages that carry the product, and the
// three thinnest on the site. The six document pages had 226 to 730.
//
// The fix is words, and the trap is that **words the prerenderer writes and no page renders are
// cloaking**, which `src/seo/prerender.ts` forbids in as many words and which nothing else here
// would have caught. So the copy lives once, in `pageCopy.ts`, and both readers draw it: the
// prerenderer for a crawler and this component for a visitor. Neither can drift, because there is
// only one text.
//
// It deliberately mirrors `CopyPage`'s section markup rather than sharing a helper with it. That is
// a small duplication and it is the right one: `CopyPage` renders a *whole document* — heading,
// lede, sections, questions and a policy date — and this renders a strip under something else. A
// shared component would have to grow a flag to say which, and the flag would be the bug.

import { Link } from 'react-router'
import type { CopySection } from '../../seo/pageCopy'

/**
 * @param sections What to draw. Absent or empty renders nothing at all — a route may carry no copy,
 *   and an empty `<div>` under a machine is 08 §1's "all that stuff" in miniature.
 */
export function CopySections({
  sections,
}: {
  // `| undefined` explicitly, because `exactOptionalPropertyTypes` is on and every call site passes
  // `PAGE_COPY[route]?.below` — a lookup that may be absent, which is the whole point of the prop.
  readonly sections?: readonly CopySection[] | undefined
}): React.JSX.Element | null {
  if (sections === undefined || sections.length === 0) return null
  return (
    <div className="page copy copy--below">
      {sections.map((section) => (
        <section className="copy__section" key={section.heading}>
          <h2>{section.heading}</h2>
          {(section.paragraphs ?? []).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.bullets === undefined ? null : (
            <ul className="copy__list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
          {section.link === undefined ? null : (
            <p>
              <Link to={section.link.to} title={section.link.text}>
                {section.link.text}
              </Link>
            </p>
          )}
        </section>
      ))}
    </div>
  )
}
